// Guided tour (spec §36): six short stops over the real interface — spotlight on the element, one sentence,
// Next / Skip. Shown once after the guided setup (setting `tour.pending`), and again on request from Settings.
import { useEffect, useLayoutEffect, useState } from 'react';
import { saveSetting, getSetting } from '@vero/domain';
import { useRuntime } from './runtime.tsx';

const STOPS = [
  { target: 'space-today', key: 'today' },
  { target: 'space-people', key: 'people' },
  { target: 'space-growth', key: 'growth' },
  { target: 'space-cases', key: 'cases' },
  { target: 'lens-trigger', key: 'lens' },
  { target: 'topbar-flow', key: 'flow' },
  { target: 'space-system', key: 'system' },
] as const;

export function Tour() {
  const rt = useRuntime();
  const pending = getSetting<boolean>(rt.view, 'tour.pending', false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const stop = STOPS[i];
  useLayoutEffect(() => {
    if (!pending || !stop) return;
    const place = () => { const el = document.querySelector(`[data-testid="${stop.target}"]`); setRect(el && (el as HTMLElement).offsetParent !== null ? el.getBoundingClientRect() : null); };
    place();
    window.addEventListener('resize', place); window.addEventListener('scroll', place, true);
    return () => { window.removeEventListener('resize', place); window.removeEventListener('scroll', place, true); };
  }, [pending, i]);
  const end = () => { setI(0); void rt.ws.run((v, c) => saveSetting(v, 'tour.pending', false, c)).catch(() => undefined); };
  useEffect(() => {
    if (!pending) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') end(); if (e.key === 'ArrowRight' || e.key === 'Enter') { e.preventDefault(); setI((x) => (x + 1 < STOPS.length ? x + 1 : (end(), 0))); } if (e.key === 'ArrowLeft') setI((x) => Math.max(0, x - 1)); };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [pending]);
  if (!pending || !stop || rt.ws.readOnlyReason) return null;
  const pad = 6;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200, vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const W = Math.min(340, vw - 32);
  const below = !rect || rect.top < vh / 2;
  const left = rect ? Math.min(Math.max(16, rect.left + rect.width / 2 - W / 2), vw - W - 16) : (vw - W) / 2;
  const top = rect ? (below ? rect.bottom + 14 : undefined) : vh / 2 - 80;
  const bottom = rect && !below ? vh - rect.top + 14 : undefined;
  return (
    <div className="tour" data-testid="tour">
      {rect && <div className="tour-spot" style={{ left: rect.left - pad, top: rect.top - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }} aria-hidden="true" />}
      {!rect && <div className="tour-dim" aria-hidden="true" />}
      <div className="tour-card card" role="dialog" aria-live="polite" aria-label={rt.t('tour.title')} style={{ left, width: W, top, bottom }}>
        <div className="tiny muted">{rt.t('tour.step', { n: i + 1, total: STOPS.length })}</div>
        <b>{rt.t(`tour.${stop.key}.title`)}</b>
        <p className="small" style={{ margin: '4px 0 10px' }}>{rt.t(`tour.${stop.key}.body`)}</p>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn btn-sm btn-ghost" onClick={end} data-testid="tour-skip">{rt.t('tour.skip')}</button>
          <span className="spacer" />
          {i > 0 && <button className="btn btn-sm" onClick={() => setI(i - 1)}>{rt.t('common.back')}</button>}
          <button className="btn btn-sm btn-primary" onClick={() => (i + 1 < STOPS.length ? setI(i + 1) : end())} data-testid="tour-next" autoFocus>{rt.t(i + 1 < STOPS.length ? 'common.next' : 'tour.finish')}</button>
        </div>
      </div>
    </div>
  );
}
